































































































































































































import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { update_agency_invoice } from '@/api/invoice';
import { getOctbSetting, get_octb_setting } from '@/api/setting';
import { formatDate, stringToIDate, formatApiError } from '@/utils/index';
import { UserModule } from '@/store/modules/user';

type BankAccountItemType = {
  text: string;
  value: number;
};

type ItemType = {
  text: string;
  value: string;
};

@Component({
  name: 'InvoiceAgencyDetailOperationConfirmPayment',
})
export default class InvoiceAgencyDetailOperationConfirmPayment extends Vue {
  public static readonly invoiceAgencyOperationConfirmPaymentEvent =
    'invoice-agency-operation-confirm-payment';
  @Prop({ default: true }) flat!: boolean;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ required: true }) currency!: string;
  @Prop({ required: true }) totalPrice!: number;
  private readonly today = new Date(); // TODO: naive implementation in despite of date change in midnight
  private bankAccountItems: BankAccountItemType[] = [];
  private bankAccounts: get_octb_setting.BankAccount[] = [];
  private valid = false;
  private operator = '';
  private operatorRules: ((v: string) => boolean | string)[] = [];
  private actualAmount = 0;
  private actualAmountRules: ((v: number | null) => boolean | string)[] = [];
  private date = '';
  private dateRules: ((v: string) => boolean | string)[] = [];
  private dateMenu = false;
  private bankAccountIndex? = 0;
  private bankAccountIndexRules: ((v: number) => boolean | string)[] = [];
  private businessCodeItems: ItemType[] = [];
  private businessCode = '';
  private businessCodeRules: ((v: string) => boolean | string)[] = [];
  private accountCodeItems: ItemType[] = [];
  private accountCode = '';
  private accountCodeRules: ((v: string) => boolean | string)[] = [];
  private auxiliaryCodeItems: ItemType[] = [];
  private auxiliaryCode = '';
  private auxiliaryCodeRules: ((v: string) => boolean | string)[] = [];
  private note = '';
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get discrepancy() {
    return this.actualAmount >= this.totalPrice
      ? `${(this.actualAmount - this.totalPrice).toLocaleString()}`
      : `▲${(this.totalPrice - this.actualAmount).toLocaleString()}`;
  }

  get canConfirmPayment() {
    return this.valid;
  }

  mounted() {
    this.fetchData().then(() => this.initializeInput());
    this.loadDefaultOperatedBy();
    this.loadRules();
  }

  private loadDefaultOperatedBy() {
    this.operator = UserModule.defaultOperatedBy;
  }

  private async fetchData() {
    AppModule.StartProgress();
    await this.loadItems();
    AppModule.StopProgress();
  }

  private initializeInput() {
    this.actualAmount = this.totalPrice;
    this.date = formatDate(this.today)!;
    this.bankAccountIndex =
      this.bankAccountItems && this.bankAccountItems.length > 0
        ? this.bankAccountItems[0].value
        : undefined;
  }

  private async loadItems() {
    try {
      const res = await getOctbSetting();
      this.loadBankAccountItems(res.bankAccounts);
      this.loadBusinessCodeItems(res.businessCodes);
      this.loadAccountCodeItems(res.accountCodes);
      this.loadAuxiliaryCodeItems(res.auxiliaryCodes);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('OCTB setting'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private loadBankAccountItems(bankAccounts: get_octb_setting.BankAccount[]) {
    this.bankAccounts = bankAccounts;
    const items: BankAccountItemType[] = [];
    this.bankAccounts.forEach((a, index) => {
      items.push({
        text: `${a.bankName} ${a.branchName} ${a.accountName}`,
        value: index,
      });
    });
    this.bankAccountItems = items;
  }

  private loadBusinessCodeItems(businessCodes: string[]) {
    const items: ItemType[] = [];
    businessCodes.forEach(c => {
      items.push({
        text: c,
        value: c,
      });
    });
    this.businessCodeItems = items;
  }

  private loadAccountCodeItems(accountCodes: string[]) {
    const items: ItemType[] = [];
    accountCodes.forEach(c => {
      items.push({
        text: c,
        value: c,
      });
    });
    this.accountCodeItems = items;
  }

  private loadAuxiliaryCodeItems(auxiliaryCodes: string[]) {
    const items: ItemType[] = [];
    auxiliaryCodes.forEach(c => {
      items.push({
        text: c,
        value: c,
      });
    });
    this.auxiliaryCodeItems = items;
  }

  private loadRules() {
    this.operatorRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Operated by'),
        }) as string),
    ];
    this.actualAmountRules = [
      (v: number | null) =>
        v !== null ||
        (this.$t('{msg} is required', {
          msg: this.$t('Actual payment amount'),
        }) as string),
      (v: number | null) =>
        v === null ||
        v >= 0 ||
        (this.$t('{msg} must be equal or greater than {num}', {
          msg: this.$t('Actual payment amount'),
          num: 0,
        }) as string),
    ];
    this.dateRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Payment date'),
        }) as string),
    ];
    this.bankAccountIndexRules = [
      (v?: number) =>
        (v !== void 0 && v >= 0) ||
        (this.$t('{msg} is required', {
          msg: this.$t('Payment account'),
        }) as string),
    ];
    this.businessCodeRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Business code'),
        }) as string),
    ];
    this.accountCodeRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Account code'),
        }) as string),
    ];
    this.auxiliaryCodeRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Auxiliary code'),
        }) as string),
    ];
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForReset() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.reset();
    };
    this.dialog = true;
  }

  private showDialogForConfirmPayment() {
    this.dialogTextTitle = `${this.$t(
      'InvoiceOperationEnum.INVOICE_CONFIRM_PAYMENT'
    )}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.confirmPayment();
    };
    this.dialog = true;
  }

  private reset() {
    (this.$refs.form as any).reset();
  }

  private confirmPayment() {
    this.$emit(
      InvoiceAgencyDetailOperationConfirmPayment.invoiceAgencyOperationConfirmPaymentEvent,
      {
        operatorName: this.operator,
        confirmPayment: {
          actualAmount: this.actualAmount!,
          date: stringToIDate(this.date),
          bankNumber: this.bankAccounts[this.bankAccountIndex!].bankNumber,
          bankName: this.bankAccounts[this.bankAccountIndex!].bankName,
          branchNumber: this.bankAccounts[this.bankAccountIndex!].branchNumber,
          branchName: this.bankAccounts[this.bankAccountIndex!].branchName,
          accountNumber: this.bankAccounts[this.bankAccountIndex!]
            .accountNumber,
          accountName: this.bankAccounts[this.bankAccountIndex!].accountName,
          businessCode: this.businessCode,
          accountCode: this.accountCode,
          auxiliaryCode: this.auxiliaryCode,
        },
        note: this.note,
      } as Partial<update_agency_invoice.Request>
    );
  }
}
